/**
 * Cloudflare Turnstile hook for invisible CAPTCHA
 */

import { useCallback, useRef, useEffect } from "react";

// Turnstile site key - this is public and safe to expose
// For development, use the test keys: https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"; // Test key (always passes)

// Type declarations for Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          size?: "normal" | "compact" | "flexible";
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      execute: (container: string | HTMLElement, options?: object) => void;
    };
  }
}

interface UseTurnstileResult {
  getToken: () => Promise<string>;
  isReady: boolean;
}

/**
 * Hook to use Cloudflare Turnstile for invisible CAPTCHA
 * Returns a function to get a fresh token when needed
 */
export function useTurnstile(): UseTurnstileResult {
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tokenResolveRef = useRef<((token: string) => void) | null>(null);
  const isReadyRef = useRef(false);

  // Create container element on mount
  useEffect(() => {
    // Create hidden container for Turnstile widget
    const container = document.createElement("div");
    container.id = "turnstile-container";
    container.style.display = "none";
    document.body.appendChild(container);
    containerRef.current = container;

    // Wait for Turnstile script to load
    const initTurnstile = () => {
      if (window.turnstile && containerRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (tokenResolveRef.current) {
              tokenResolveRef.current(token);
              tokenResolveRef.current = null;
            }
          },
          "error-callback": () => {
            if (tokenResolveRef.current) {
              // Return empty string on error - backend will handle
              tokenResolveRef.current("");
              tokenResolveRef.current = null;
            }
          },
          "expired-callback": () => {
            // Token expired, will get fresh one on next request
          },
        });
        isReadyRef.current = true;
      }
    };

    // Check if already loaded
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for script to load
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          initTurnstile();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }

    return () => {
      // Cleanup
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      if (containerRef.current) {
        containerRef.current.remove();
      }
    };
  }, []);

  // Get a fresh token (triggers invisible challenge)
  const getToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      if (!window.turnstile || !widgetIdRef.current) {
        // Turnstile not loaded, return empty (backend will handle)
        console.warn("[Turnstile] Not ready, skipping verification");
        resolve("");
        return;
      }

      // Reset and get fresh token
      tokenResolveRef.current = resolve;
      window.turnstile.reset(widgetIdRef.current);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (tokenResolveRef.current) {
          tokenResolveRef.current("");
          tokenResolveRef.current = null;
        }
      }, 30000);
    });
  }, []);

  return {
    getToken,
    isReady: isReadyRef.current,
  };
}
